import { useEffect, useRef } from 'react';

import { Flag32 as Flag } from '@weston/react-world-flags';
import { Leg } from 'seacube-backend/src/api/client';
import { useSnapshot } from 'valtio';

import { Badge, Tooltip } from '@mui/material';
import { Box, Paper, Stack, Typography } from '@mui/material';

import { DataGrid, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';

import AnchorTwoToneIcon from '@mui/icons-material/AnchorTwoTone';
import BoltTwoToneIcon from '@mui/icons-material/BoltTwoTone';
import DirectionsBoatFilledTwoToneIcon from '@mui/icons-material/DirectionsBoatFilledTwoTone';
import LocalShippingTwoToneIcon from '@mui/icons-material/LocalShippingTwoTone';
import PowerIcon from '@mui/icons-material/PowerOff';
import PrecisionManufacturingTwoToneIcon from '@mui/icons-material/PrecisionManufacturingTwoTone';
import TableRowsTwoToneIcon from '@mui/icons-material/TableRowsTwoTone';
import TrainIcon from '@mui/icons-material/Train';
import WarehouseTwoToneIcon from '@mui/icons-material/WarehouseTwoTone';
import WhatshotIcon from '@mui/icons-material/Whatshot';

import {
  LegAugmented,
  clearSelectedLegId,
  resetHighlightedGpsDataFilteredIndextGps,
  setHighlightedGpsDataFilteredIndex,
  setSelectedLegId,
  setTsFilterRange,
  store,
} from '../store';
import { setSelectLegs } from '../store/events';
import { humanize } from '../utils/time';

function getEventIcon(eventType: any) {
  switch (eventType) {
    case 'VESSEL_AT_BERTH':
      return <AnchorTwoToneIcon />;
    case 'VESSEL_AT_SEA':
      return <DirectionsBoatFilledTwoToneIcon />;
    case 'HGV':
      return <LocalShippingTwoToneIcon />;
    case 'CRANE':
      return <PrecisionManufacturingTwoToneIcon />;
    case 'POWER_OUTAGE':
      return <BoltTwoToneIcon />;
    case 'CONTAINER_ZONE':
      return <TableRowsTwoToneIcon />;
    case 'LOGISTIC_SITE':
      return <WarehouseTwoToneIcon />;
    case 'TRAIN':
      return <TrainIcon />;
    default:
      return null;
  }
}

// Ajouter cette fonction en haut du fichier pour formater la date
const formatStartTime = (dateString: string) => {
  const date = new Date(dateString);
  return date
    .toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false, // Pour utiliser le format 24h
    })
    .replace(',', ''); // Pour enlever la virgule que le format anglais ajoute
};

export const EventTimeline = () => {
  const { legsAugmented, selectedLegIds, reeferId } = useSnapshot(store);

  // Référence pour l'élément de scroll
  const scrollRef = useRef<HTMLDivElement>(null);

  // Gestionnaire de scroll avec passive: false
  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      element.scrollLeft += e.deltaY;
    };

    element.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      element.removeEventListener('wheel', handleWheel);
    };
  }, []);

  const handleBlur = () => {
    clearSelectedLegId();

    const start = 0;
    const end = 0;
    // setGpsEventLayerFilter([start, end]);
    setTsFilterRange([start, end]);
    setSelectLegs([]);
  };

  const handleFocus = (leg: Leg) => {
    let start = Date.parse(leg['start_datetime']); // Timestamp in milliseconds
    let end = Date.parse(leg['end_datetime']); // Timestamp in milliseconds
    setSelectedLegId(leg.id);
    // setGpsEventLayerFilter([start, end]);
    resetHighlightedGpsDataFilteredIndextGps();
    setTsFilterRange([start, end]);
    setSelectLegs([leg]);
  };

  const handleClick = (ids: GridRowSelectionModel) => {
    let selectedEventId: string | null = null;
    if (ids.length > 0) {
      selectedEventId = ids[0].toString();
      const selectedEvent = legsAugmented.filter(
        (row) => row.id.toString() == selectedEventId,
      )[0];
      handleFocus(selectedEvent);
    } else {
      handleBlur();
    }
  };

  useEffect(() => {
    if (!legsAugmented) return;
    if (legsAugmented.length > 0) {
      handleFocus(legsAugmented[0]);
      setSelectLegs([legsAugmented[0]]);
    }
  }, [legsAugmented]);

  return (
    <div
      style={{
        width: '100%',
        height: '110px',
        backgroundColor: '#fff',

        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        ref={scrollRef}
        className="timeline-scroll"
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '8px 16px',
          overflowX: 'auto',
          overflowY: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '8px',
            minWidth: 'min-content', // Permet au contenu de définir la largeur
          }}
        >
          {legsAugmented.map((leg, index) => (
            <div
              key={leg.id}
              onClick={() => handleClick([leg.id])}
              style={{
                position: 'relative',
                flex: '0 0 95px',
                padding: '12px',
                cursor: 'pointer',
                height: '95px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '4px',
                borderRadius: '8px',
                backgroundColor: selectedLegIds.includes(leg.id)
                  ? '#e3f2fd'
                  : '#fff',
                border: selectedLegIds.includes(leg.id)
                  ? '1px solid #1976d2'
                  : '1px solid #e0e0e0',
                boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              }}
            >
              {leg.events.powerOutage > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '0px',
                    left: '0px',
                    backgroundColor: '#d32f2f',
                    color: 'white',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  {leg.events.powerOutage}
                </div>
              )}
              {leg.events.temperatureBreach > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    top: '0px',
                    right: '0px',
                    backgroundColor: '#ed6c02',
                    color: 'white',
                    borderRadius: '50%',
                    width: '22px',
                    height: '22px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.75rem',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                  }}
                >
                  {leg.events.temperatureBreach}
                </div>
              )}

              <div
                style={{
                  color: selectedLegIds.includes(leg.id) ? '#1976d2' : '#333',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '1.8rem',
                  marginTop: '4px',
                }}
              >
                {getEventIcon(leg.event_type)}
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '3px',
                }}
              >
                <span
                  style={{
                    fontSize: '0.85rem',
                    fontWeight: 'bold',
                    color: selectedLegIds.includes(leg.id) ? '#1976d2' : '#666',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {humanize(leg.duration)}
                </span>
                <span
                  style={{
                    fontSize: '0.75rem',
                    color: '#999',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  {formatStartTime(leg.start_datetime)}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <style>
        {`
          .timeline-scroll {
            scrollbar-width: thin;
            scrollbar-color: #888 #f1f1f1;
          }
          .timeline-scroll::-webkit-scrollbar {
            height: 6px;
          }
          .timeline-scroll::-webkit-scrollbar-track {
            background-color: #f1f1f1;
            border-radius: 3px;
          }
          .timeline-scroll::-webkit-scrollbar-thumb {
            background-color: #888;
            border-radius: 3px;
          }
          .timeline-scroll::-webkit-scrollbar-thumb:hover {
            background-color: #666;
          }
        `}
      </style>
    </div>
  );
};
